import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { defer, Observable } from "rxjs";
import { AuthApiModel } from "../../api/models/auth.api.model";
import { finalize, shareReplay, tap } from "rxjs/operators";
import { isPlatformBrowser } from "@angular/common";
import { AuthenticationRestService } from "../../api/services/authentication.rest.service";
import { StorageService } from "./storage.service";
import { JwtHelperService } from "@auth0/angular-jwt";
import { NativeSecureSessionService } from '../mobile/native-secure-session.service';

@Injectable({
  providedIn: 'root'
})
export class JwtService {

  public token: string;
  public refreshToken: string;
  private remember: boolean;
  private refreshRequest$?: Observable<AuthApiModel>;
  private sessionVersion = 0;

  public get sessionRevision(): number {
    return this.sessionVersion;
  }
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly jwt: JwtHelperService,
    private readonly as: AuthenticationRestService,
    private readonly storage: StorageService,
    private readonly nativeSecureSession: NativeSecureSessionService,
  ) {
    this.getTokenFromStorage();
  }

  public refreshJwtToken(): Observable<AuthApiModel> {
    if (!this.refreshRequest$) {
      const request$ = defer(() => {
        this.getTokenFromStorage();
        const version = this.sessionVersion;
        const token = this.token;
        const remember = this.hasRemember();
        return this.as.postRefreshToken(this.refreshToken).pipe(tap(response => {
          this.getTokenFromStorage();
          // A refresh started before logout/login must not restore the old session.
          if (version !== this.sessionVersion || this.token !== token) {
            throw new Error('Session changed during token refresh');
          }
          this.persistSession(response, remember);
        }));
      }).pipe(
        finalize(() => {
          if (this.refreshRequest$ === request$) this.refreshRequest$ = undefined;
        }),
        shareReplay({bufferSize: 1, refCount: false})
      );
      this.refreshRequest$ = request$;
    }
    return this.refreshRequest$;
  }

  public getTokenFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.token = this.storage.getItem('token');
      this.refreshToken = this.storage.getItem('refresh_token');
    }
  }

  private setToken(response: AuthApiModel): this {
    const data = this.jwt.decodeToken(response.token);
    this.token = response.token;
    this.storage.setItem('token', this.token);
    this.storage.setItem('token_expiration', JSON.stringify({ exp: data.exp, iat: data.iat }));
    void this.nativeSecureSession.write({
      token: this.token,
      tokenExpiration: JSON.stringify({ exp: data.exp, iat: data.iat }),
    });
    return this;
  }

  public setSession(response: AuthApiModel, remember: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      this.sessionVersion++;
      this.refreshRequest$ = undefined;
      this.persistSession(response, remember);
    }
  }

  private persistSession(response: AuthApiModel, remember: boolean): void {
    this.setToken(response);
    if (remember) {
      this.setRemember(response);
    } else {
      this.clearRemember();
    }
  }

  public setRemember(response: AuthApiModel): this {
    if (response.refreshToken) {
      this.refreshToken = response.refreshToken;
      this.storage.setItem('refresh_token', this.refreshToken);
      void this.nativeSecureSession.write({refreshToken: this.refreshToken});
    }
    this.remember = true;
    this.storage.setItem('remember', 'true');
    void this.nativeSecureSession.write({remember: 'true'});
    return this;
  }

  public clearRemember(): this {
    this.refreshToken = null;
    this.remember = false;
    this.storage.removeItem('refresh_token');
    this.storage.setItem('remember', 'false');
    void this.nativeSecureSession.remove(['refreshToken']);
    void this.nativeSecureSession.write({remember: 'false'});
    return this;
  }

  public hasRemember() {
    let ls: String = '';
    if (isPlatformBrowser(this.platformId)) {
      ls = this.storage.getItem('remember');
    }
    return ls === 'true';
  }

  public canRefresh(): boolean {
    this.getTokenFromStorage();
    return this.hasRemember() && !!this.refreshToken
      && this.refreshToken !== 'null' && this.refreshToken !== 'undefined';
  }

  public clearAllTokenRelatedFromStorage(): void {
    // clear token remove user from to log user out
    if (isPlatformBrowser(this.platformId)) {
      this.sessionVersion++;
      this.refreshRequest$ = undefined;
      this.token = null;
      this.refreshToken = null;
      this.remember = false;
      this.storage.removeItem('token');
      this.storage.removeItem('token_expiration');
      this.storage.removeItem('refresh_token');
      this.storage.removeItem('remember');
      void this.nativeSecureSession.clear();
    }
  }

  public decodeToken(token: string) {
    return this.jwt.decodeToken(token);
  }

  public isTokenExpired(): boolean | Promise<boolean> {
    this.getTokenFromStorage()
    return this.jwt.isTokenExpired(this.token);
  }
}
